import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { documentsAPI } from '../api/client';
import CountBar from './CountBar';
import { extractErrorMessage } from '../utils/errors';

/**
 * The Start Page's three widgets: "Recently Changed" and "Most Referenced"
 * (GitHub issue #173), plus "Recently Viewed" (issue #188) -- all three
 * following the page's own My Locations / All Locations selection, except
 * Recently Viewed, which is inherently the caller's own history and takes no
 * scope of its own.
 *
 * They live in one file because they are one row of the layout and share
 * most of their loading/fetch machinery; splitting them would mean three
 * components, three imports and three near-identical loading branches to
 * keep in step for no reader's benefit.
 *
 * ACTIVE ONLY for Recently Changed and Most Referenced -- issue #173 says so
 * of this whole page, and the backend enforces it either way. Recently
 * Viewed is deliberately NOT restricted to ACTIVE: issue #188 never asked
 * for that, and a caller's own view history legitimately includes drafts
 * they can read.
 */

const ROW_LIMIT = 8;
const REFERENCE_WINDOW_DAYS = 90;

function WidgetShell({ title, action, loading, error, empty, children }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>{title}</h3>
        {action}
      </div>
      {loading && <div className="loading">Loading…</div>}
      {!loading && error && <div className="alert alert-error">{error}</div>}
      {!loading && !error && empty}
      {!loading && !error && !empty && children}
    </div>
  );
}

function StartPageWidgets({ scope }) {
  const [recent, setRecent] = useState([]);
  const [referenced, setReferenced] = useState([]);
  const [viewed, setViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [recentRes, referencedRes, viewedRes] = await Promise.all([
        documentsAPI.list({ status: ['active'], scope, limit: ROW_LIMIT }),
        documentsAPI.mostReferenced({ scope, days: REFERENCE_WINDOW_DAYS, limit: ROW_LIMIT }),
        documentsAPI.recentlyViewed({ limit: ROW_LIMIT }),
      ]);
      setRecent(recentRes.data);
      setReferenced(referencedRes.data.rows || []);
      setViewed(viewedRes.data.rows || []);
      setError(null);
    } catch (err) {
      console.error('Error loading start page widgets:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const maxViews = referenced.reduce((max, row) => Math.max(max, row.view_count), 0);

  return (
    <div className="stats-grid-triple">
      {/* NO "View all" ACTION HERE, deliberately (issue #201). It used to link
          to /documents?status=active, gated on can('document_browse','read') --
          and that gate was correct: it is exactly equivalent to the route's own
          RequirePermission, since models.py restricts DOCUMENT_BROWSE to {READ},
          so the two cannot diverge. What the gate could NOT cover is that
          DOCUMENT_BROWSE is HIERARCHICAL, so can() means "somewhere" rather
          than "here" -- a node-scoped holder saw the link, passed the route
          gate, and could still land on a near-empty list. Removing the link
          keeps the reader on the screen they came to use. */}
      <WidgetShell
        title="Recently Changed"
        loading={loading}
        error={error}
        empty={recent.length === 0 && (
          <div className="empty-state"><p>No active documents here yet.</p></div>
        )}
      >
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {recent.map(doc => (
            <li key={doc.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              {/* state.from: see SearchAnswer.js's identical comment. */}
              <Link to={`/documents/${doc.id}`} state={{ from: '/' }}>{doc.title}</Link>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                {doc.hierarchy_display_path || '—'}
                {' · '}
                {new Date(doc.updated_at).toLocaleDateString()}
              </div>
            </li>
          ))}
        </ul>
      </WidgetShell>

      <WidgetShell
        title="Most Referenced"
        loading={loading}
        error={error}
        empty={referenced.length === 0 && (
          <div className="empty-state">
            {/* Distinguished from "no policies" on purpose: an empty list here
                usually means nobody has opened one yet in this window, which is
                a different thing from an empty corpus. */}
            <p>Nothing has been opened here in the last {REFERENCE_WINDOW_DAYS} days.</p>
          </div>
        )}
      >
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {referenced.map(row => (
            <li key={row.document_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <Link to={`/documents/${row.document_id}`} state={{ from: '/' }}>{row.title}</Link>
              <div style={{ fontSize: '12px', color: 'var(--muted)', margin: '2px 0 4px' }}>
                {row.hierarchy_display_path || '—'}
              </div>
              {/* Scaled to this list's own maximum -- CountBar's rule. */}
              <CountBar value={row.view_count} max={maxViews} />
            </li>
          ))}
        </ul>
        <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '10px 0 0' }}>
          Times opened in the last {REFERENCE_WINDOW_DAYS} days. Counts only, never who.
        </p>
      </WidgetShell>

      <WidgetShell
        title="Recently Viewed"
        loading={loading}
        error={error}
        empty={viewed.length === 0 && (
          <div className="empty-state"><p>You haven't opened anything here yet.</p></div>
        )}
      >
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {viewed.map(row => (
            <li key={row.document_id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
              <Link to={`/documents/${row.document_id}`} state={{ from: '/' }}>{row.document_title}</Link>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                {row.hierarchy_display_path || '—'}
                {' · '}
                {new Date(row.viewed_at).toLocaleDateString()}
              </div>
            </li>
          ))}
        </ul>
      </WidgetShell>
    </div>
  );
}

export default StartPageWidgets;
